import {
    type App,
    type DateRange,
    EmployeeStatus,
    type EntityFilter,
    GetBalancesParams,
    getRange,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";

export const balancesKey = ["balances", "all-employees"] as const;

/** The year of `date`, cut off at the end of the current month: later balances don't exist yet. */
export function balancesYearRange(date: DateString): DateRange {
    const range = getRange(date, "year");
    const currMonth = getRange(toDateString(new Date()), "month");
    if (range.to > currMonth.to) {
        range.to = currMonth.to;
    }
    return range;
}

/**
 * Not `app.employeeFilters`: the ledgers overview always covers every accessible employee with a contract in the
 * range. The ids are part of the query key, so a change in the accessible employees refetches.
 */
function employeeFilters(app: App, range: DateRange): EntityFilter[] {
    return app.accessibleEmployees
        .filter((e) => e.status !== EmployeeStatus.Probation && !!e.getContractForRange(range))
        .map((e) => ({ type: "employeeId", value: e.id }) as EntityFilter);
}

export function balancesQuery(app: App, { from, to }: DateRange) {
    const filters = employeeFilters(app, { from, to });
    return {
        queryKey: [...balancesKey, from, to, filters] as const,
        queryFn: ({ signal }: { signal: AbortSignal }) =>
            app.api.getBalances(new GetBalancesParams({ filters, from, to, resolution: "month" }), { signal }),
    };
}
